import { useEffect, useMemo, useRef, useState } from "react";
import { Check, FileText, Image as ImageIcon, Loader2, X } from "lucide-react";

import { Icon as BrandIcon } from "@/lib/Icon";
import {
  useHealthieForm,
  useIntakeDraft,
  useSubmitHealthieIntake,
  useUploadDocument,
} from "@/lib/api/hooks";
import { track } from "@/lib/analytics";
import { PageHeader } from "@/components/app-shell";
import {
  IntakeFormField as ModuleField,
  archetypeForModule,
  focusIntakeField,
} from "./IntakeFormField";
import {
  disqualifiedHealthieModules,
  healthieExclusiveOptions,
  healthieServerFieldErrors,
  hydrateHealthieChoiceAnswer,
  visibleHealthieAnswers,
  visibleHealthieModules,
} from "./healthieFormRules";
import {
  CARRIED_KEY,
  readCarriedMeta,
  shortProductName,
  withoutCarried,
  type SubmittedIntake,
} from "./intakeCarry";
import { useHealthieDraftAutosave } from "./useHealthieDraftAutosave";

interface HealthieFormRendererProps {
  formId: string;
  productName?: string;
  productId?: string;
  /** Receives the submitted answers so the wizard can carry shared ones forward. */
  onSubmitted?: (
    submitted: Omit<SubmittedIntake, "productName">,
  ) => void | Promise<void>;
  mode?: "complete" | "preview";
  /** Nothing follows this form, so its button submits the whole intake. */
  finalStep?: boolean;
  /** Position among several intakes, shown above the form title. */
  stepLabel?: string;
  /** Confirms the previous form was sent, so this one doesn't read as a retry. */
  notice?: string;
}

export function HealthieFormRenderer({
  formId,
  productName,
  productId,
  onSubmitted,
  mode = "complete",
  finalStep = false,
  stepLabel,
  notice,
}: HealthieFormRendererProps) {
  const preview = mode === "preview";
  const { data: form, isLoading, error } = useHealthieForm(formId, {
    personalize: !preview,
  });
  const submit = useSubmitHealthieIntake();
  const evidenceUpload = useUploadDocument();
  const draft = useIntakeDraft(`healthie-${formId}`, { enabled: !preview });
  // PUT replaces the draft. Until the stored draft has loaded into this form,
  // a save merges it in first so answers the patient hasn't seen survive.
  const autosave = useHealthieDraftAutosave(`healthie-${formId}`, {
    enabled: !preview,
    baseLoaded: draft.isSuccess,
  });
  const carried = readCarriedMeta(draft.data?.answers);
  const [carriedExpanded, setCarriedExpanded] = useState(false);
  // Covers waiting for saves to land, the submit itself and the wizard's
  // carry step, so Continue can't fire twice in between.
  const [finishing, setFinishing] = useState(false);
  const submittingRef = useRef(false);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [serverValidation, setServerValidation] = useState<{
    error: unknown;
    answers: Record<string, string>;
  } | null>(null);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [prescriptionEvidence, setPrescriptionEvidence] = useState<File | null>(
    null,
  );
  const [evidenceUploaded, setEvidenceUploaded] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const showTransferEvidence =
    !preview &&
    String(productId ?? "")
      .toLowerCase()
      .startsWith("tirzepatide-");

  // Prefill once from the server-side draft. The web-intake mirror seeds
  // intake_drafts (form_slug `healthie-{formId}`, keys = live module ids)
  // when the marketing-site answers only PARTIALLY matched a drifted
  // Healthie form — the patient then answers just the unmatched delta here
  // instead of redoing the whole intake. Unknown module ids are ignored;
  // radio values must match a rendered option (trim/case-insensitive);
  // anything the user already typed wins over the draft.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    const draftAnswers = draft.data?.answers;
    if (!form || !draftAnswers) return;
    const valid: Record<string, string> = {};
    for (const m of form.modules) {
      const v = draftAnswers[m.id];
      if (typeof v !== "string" || !v.trim()) continue;
      const arch = archetypeForModule(m.mod_type);
      if (arch === "header" || arch === "signature" || arch === "agree")
        continue;
      if (arch === "radio" || arch === "checkbox") {
        const match = hydrateHealthieChoiceAnswer(m, v);
        if (!match) continue;
        valid[m.id] = match;
      } else {
        valid[m.id] = v;
      }
    }
    if (Object.keys(valid).length === 0) return;
    prefilledRef.current = true;
    setAnswers((prev) => ({ ...valid, ...prev }));
  }, [form, draft.data]);

  const visibleModules = useMemo(
    () => visibleHealthieModules(form?.modules ?? [], answers),
    [form, answers],
  );
  const disqualifiedModules = useMemo(
    () => disqualifiedHealthieModules(form?.modules ?? [], answers),
    [form, answers],
  );
  const disqualifiedIds = new Set(
    disqualifiedModules.map((module) => module.id),
  );
  const serverFieldErrors = new Map(
    healthieServerFieldErrors(serverValidation?.error, visibleModules)
      .filter(
        ({ moduleId }) =>
          serverValidation?.answers[moduleId] === answers[moduleId],
      )
      .map(({ moduleId, message }) => [moduleId, message]),
  );

  // Validate visible required modules — input fields only (display-only modules can
  // be unanswered without blocking submit).
  const missing = useMemo(() => {
    return visibleModules.filter((m) => {
      if (!m.required) return false;
      const arch = archetypeForModule(m.mod_type);
      if (arch === "header") return false;
      return !(answers[m.id] && answers[m.id].trim().length > 0);
    });
  }, [visibleModules, answers]);

  const edited = useRef(false);
  // Answers changed on this form. A carried answer the patient changes here
  // is theirs from then on, so a later intake's carry never replaces it.
  const editedIds = useRef(new Set<string>());
  const setAnswer = (id: string, val: string) => {
    edited.current = true;
    editedIds.current.add(id);
    setAnswers((prev) => ({ ...prev, [id]: val }));
  };
  const draftPayload = (): Record<string, unknown> => {
    const meta = withoutCarried(carried, editedIds.current);
    return meta ? { ...answers, [CARRIED_KEY]: meta } : { ...answers };
  };

  // Save the whole answer set (plus carry metadata, which must survive the
  // replace) after the patient changes something. A restored draft alone is
  // already stored, so it never triggers a save.
  const lastScheduled = useRef<string | null>(null);
  const schedule = autosave.schedule;
  useEffect(() => {
    if (!edited.current || preview) return;
    const payload = draftPayload();
    const serialized = JSON.stringify(payload);
    if (serialized === lastScheduled.current) return;
    lastScheduled.current = serialized;
    schedule(payload);
    // `carried` is derived from the draft; its identity changes with every
    // cache write, its content does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, preview, schedule]);

  // Flips on the first blocked submit attempt. Until then required fields
  // only carry the asterisk; after it, every still-missing field shows a
  // red "required" line and the footer counts them — the asterisk alone is
  // too easy to miss when you reach the bottom of a long form.
  const [attempted, setAttempted] = useState(false);
  const missingIds = useMemo(
    () => new Set(missing.map((m) => m.id)),
    [missing],
  );

  // Answers carried in from an earlier intake of this purchase collapse into
  // one summary card until the patient opens them. Anything with an error,
  // or no longer answered, stays in the list so it can be fixed.
  const collapsedIds = new Set(
    carriedExpanded || !carried
      ? []
      : visibleModules
          .filter(
            (m) =>
              carried.module_ids.includes(m.id) &&
              Boolean(answers[m.id]?.trim()) &&
              !disqualifiedIds.has(m.id) &&
              !serverFieldErrors.has(m.id),
          )
          .map((m) => m.id),
  );

  const handleSubmit = async () => {
    if (!form || disqualifiedModules.length > 0 || submittingRef.current)
      return;
    if (missing.length > 0) {
      setAttempted(true);
      // Scroll the first missing field into view so the user can find it.
      focusIntakeField(`healthie-mod-${missing[0].id}`);
      return;
    }
    if (preview) {
      setPreviewMessage("Preview complete — no answers were saved.");
      return;
    }
    if (prescriptionEvidence && !evidenceUploaded) {
      setEvidenceError(null);
      try {
        await evidenceUpload.mutateAsync({
          file: prescriptionEvidence,
          purpose: "transfer_prescription",
        });
        setEvidenceUploaded(true);
      } catch (uploadError) {
        setEvidenceError(
          (uploadError as Error).message ||
            "The prescription image could not be uploaded. Remove it to continue without it, or retry.",
        );
        return;
      }
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setFinishing(true);
    const submittedAnswers = visibleHealthieAnswers(form.modules, answers);
    setServerValidation(null);
    // A save landing after submit would recreate the draft the backend deletes.
    await autosave.pause();
    try {
      await submit.mutateAsync({
        healthie_form_id: formId,
        answers: submittedAnswers,
      });
    } catch (submissionError) {
      submittingRef.current = false;
      setFinishing(false);
      autosave.resume();
      lastScheduled.current = null;
      if (edited.current) autosave.schedule(draftPayload());
      const fields = healthieServerFieldErrors(submissionError, visibleModules);
      if (fields.length > 0) {
        setServerValidation({
          error: submissionError,
          answers: submittedAnswers,
        });
        focusIntakeField(`healthie-mod-${fields[0].moduleId}`);
      }
      // Other API errors retain the existing form-level error display.
      return;
    }
    autosave.clear();
    track("intake_step_submitted", {
      form_id: formId,
      product_name: productName,
    });
    try {
      await onSubmitted?.({
        formId,
        modules: form.modules,
        answers: submittedAnswers,
      });
    } finally {
      submittingRef.current = false;
      setFinishing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-10 type-body-md text-ink-muted px-15 py-30">
        <Loader2 size={14} className="animate-spin" /> Loading intake…
      </div>
    );
  }
  if (error || !form) {
    return (
      <div className="type-body-md text-danger px-15 py-30">
        Couldn&apos;t load this intake form. Refresh to try again.
      </div>
    );
  }
  if (form.eligibility_message) {
    return (
      <div role="alert" className="type-body-md text-danger px-15 py-30">
        {form.eligibility_message}
      </div>
    );
  }

  // Name only the intakes the collapsed answers came from, without strength.
  const carriedSources = [
    ...new Set(
      [...collapsedIds]
        .flatMap((id) =>
          carried?.sources?.[id] ? [carried.sources[id]] : (carried?.from ?? []),
        )
        .map(shortProductName),
    ),
  ];
  const saveStatus =
    autosave.status === "saving"
      ? "Saving…"
      : autosave.status === "saved"
        ? "Saved"
        : "";

  // The summary card takes the place of the first carried question.
  const rows = visibleModules.filter(
    (m, index, all) =>
      !collapsedIds.has(m.id) ||
      all.findIndex((other) => collapsedIds.has(other.id)) === index,
  );

  return (
    <div className="flex-1 flex flex-col">
      <PageHeader
        eyebrow={stepLabel}
        title={form.name}
        description={
          preview
            ? "Clinician preview · interact with the exact form patients receive. Nothing entered here is saved."
            : productName
              ? `Your clinician will review your ${productName} responses before writing your script.`
              : "Your clinician will review your responses before writing your script."
        }
      />
      <div className="relative flex-1 px-15 md:px-40 pt-20 md:pt-40 pb-30 max-w-[44rem] md:max-w-[64rem] mx-auto w-full flex flex-col gap-20">
        {/* Quiet by design: a failed save shows nothing and retries on the
            next change. Sits in the top padding so it never moves the form. */}
        {!preview && (
          <span
            aria-live="polite"
            className="absolute top-5 md:top-15 right-15 md:right-40 type-label-xs text-ink-muted"
          >
            {saveStatus}
          </span>
        )}
        {notice && (
          <p
            role="status"
            className="flex items-center gap-8 border border-line-subtle bg-[var(--surface-sunken)] px-15 py-12 type-body-sm text-ink"
          >
            <Check size={14} aria-hidden className="shrink-0" />
            {notice}
          </p>
        )}
        <div className="flex flex-col">
          {rows.map((m, i) => (
            <div
              key={collapsedIds.has(m.id) ? "carried-answers" : m.id}
              className={i === 0 ? "" : "mt-25 pt-25 border-t border-[#ECECEC]"}
            >
              {collapsedIds.has(m.id) ? (
                <CarriedAnswersCard
                  count={collapsedIds.size}
                  sources={carriedSources}
                  onReview={() => setCarriedExpanded(true)}
                />
              ) : (
                <ModuleField
                  module={m}
                  value={answers[m.id] ?? ""}
                  onChange={(v) => setAnswer(m.id, v)}
                  exclusiveOptions={healthieExclusiveOptions(m)}
                  error={
                    disqualifiedIds.has(m.id)
                      ? m.disqualification_message ||
                        "Based on this answer, this treatment is not available through this intake. If you selected it by mistake, you can correct your answer."
                      : (serverFieldErrors.get(m.id) ??
                        (attempted && missingIds.has(m.id)
                          ? "THIS QUESTION IS REQUIRED"
                          : null))
                  }
                />
              )}
            </div>
          ))}
        </div>

        {showTransferEvidence && (
          <section className="border border-[#ECECEC] bg-[#FAFAFA] p-15 flex flex-col gap-10">
            <div>
              <p className="type-label-md text-ink">
                CURRENT PRESCRIPTION OR VIAL PHOTO · OPTIONAL
              </p>
              <p className="type-body-sm text-ink-secondary mt-5">
                If you are transferring an existing GLP-1/GIP dose, upload a
                clear photo of the vial/label or a PDF of the current
                prescription. Your clinician will verify this source against the
                dose you reported above.
              </p>
            </div>
            <div className="border border-dashed border-line flex items-center hover:border-ink">
              <label className="cursor-pointer min-w-0 flex-1 p-15 flex items-center gap-10">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                  className="sr-only"
                  onChange={(event) => {
                    setPrescriptionEvidence(event.target.files?.[0] ?? null);
                    setEvidenceUploaded(false);
                    setEvidenceError(null);
                    event.currentTarget.value = "";
                  }}
                />
                {prescriptionEvidence?.type.startsWith("image/") ? (
                  <ImageIcon size={20} aria-hidden />
                ) : (
                  <FileText size={20} aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className="type-body-sm font-medium block truncate">
                    {prescriptionEvidence?.name ?? "Choose photo or PDF"}
                  </span>
                  <span className="text-11 text-ink-muted">
                    {evidenceUploaded
                      ? "Uploaded and attached to your clinical chart"
                      : "JPG, PNG, HEIC, WebP, or PDF"}
                  </span>
                </span>
              </label>
              {prescriptionEvidence && (
                <button
                  type="button"
                  aria-label="Remove selected prescription evidence"
                  onClick={(event) => {
                    event.preventDefault();
                    setPrescriptionEvidence(null);
                    setEvidenceUploaded(false);
                    setEvidenceError(null);
                  }}
                  className="mr-10 p-5"
                >
                  <X size={16} aria-hidden />
                </button>
              )}
            </div>
            {evidenceError && (
              <p role="alert" className="type-body-sm text-danger">
                {evidenceError}
              </p>
            )}
          </section>
        )}

        {!preview && submit.error && !serverValidation && (
          <div role="alert" className="type-body-sm text-danger">
            {(submit.error as { message?: string }).message ?? "Submit failed"}
          </div>
        )}

        <div className="flex items-center justify-between gap-10">
          {!preview && !attempted && missing.length > 0 && (
            <span className="type-label-xs text-ink-muted">
              {missing.length} required{" "}
              {missing.length === 1 ? "question" : "questions"} left
            </span>
          )}
          {/* The top copy announces; this one is for the end of a long form. */}
          <span aria-hidden className="ml-auto type-label-xs text-ink-muted">
            {saveStatus}
          </span>
        </div>
      </div>

      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-15 py-15 md:px-40 md:py-20">
        {previewMessage && (
          <div className="type-label-xs text-success mb-8 max-w-[64rem] mx-auto">
            {previewMessage}
          </div>
        )}
        {!preview && attempted && missing.length > 0 && (
          <div
            role="alert"
            className="type-label-xs text-[#C81E1E] mb-8 max-w-[64rem] mx-auto"
          >
            {missing.length} required{" "}
            {missing.length === 1 ? "question" : "questions"} remaining — marked
            in red above
          </div>
        )}
        {disqualifiedModules.length > 0 && (
          <p className="type-body-sm text-[#C81E1E] mb-8 max-w-[64rem] mx-auto">
            This intake cannot continue. Review the eligibility message above.
          </p>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={
            disqualifiedModules.length > 0 ||
            (!preview &&
              (submit.isPending || evidenceUpload.isPending || finishing))
          }
          className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50 max-w-[64rem] mx-auto"
        >
          {preview
            ? "Finish preview (do not save)"
            : evidenceUpload.isPending
              ? "Uploading prescription evidence…"
              : submit.isPending || finishing
                ? "Submitting…"
                : finalStep
                  ? "Submit intake"
                  : "Continue"}
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-8"
          >
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
      </div>
    </div>
  );
}

function CarriedAnswersCard({
  count,
  sources,
  onReview,
}: {
  count: number;
  sources: string[];
  onReview: () => void;
}) {
  const from =
    sources.length === 1
      ? `your ${sources[0]} intake`
      : sources.length === 2
        ? `your ${sources[0]} and ${sources[1]} intakes`
        : "your earlier intakes";
  return (
    <section
      aria-label="Answers from an earlier intake"
      className="flex flex-col gap-10 xs:flex-row xs:items-center xs:justify-between border border-line-subtle bg-[var(--surface-sunken)] p-15"
    >
      <p className="type-body-sm text-ink">
        {count} {count === 1 ? "answer" : "answers"} filled in from {from}
      </p>
      <button
        type="button"
        onClick={onReview}
        className="footer-link self-start xs:self-auto min-h-44"
      >
        Review or change
      </button>
    </section>
  );
}
